import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext'; // Assuming auth context for API calls
import AddIcon from '../../components/icons/AddIcon';
import EditIcon from '../../components/icons/EditIcon';
import DeleteIcon from '../../components/icons/DeleteIcon';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const QuizManagerAdminScreen = () => {
    const { token } = useAuth();

    // Quiz Creation State
    const [newQuizName, setNewQuizName] = useState('');
    const [newQuizDescription, setNewQuizDescription] = useState('');

    // Active Quiz State (after creation or loading)
    const [quizzes, setQuizzes] = useState([]);
    const [activeQuiz, setActiveQuiz] = useState(null);
    const [editingQuizName, setEditingQuizName] = useState('');
    const [editingQuizDescription, setEditingQuizDescription] = useState('');
    const [questionsInQuiz, setQuestionsInQuiz] = useState([]);

    // Question Management State
    const [availableQuestions, setAvailableQuestions] = useState([]);
    const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);

    // New/Edit Question Form State (within modal)
    const [questionMode, setQuestionMode] = useState('selectExistingLink');
    const [selectedExistingQuestionId, setSelectedExistingQuestionId] = useState('');

    // State for creating/editing a standalone question
    const [currentQuestionData, setCurrentQuestionData] = useState({
        id: '', // This will be the question_identifier
        type: 'textInput', // Default type
        title: '',
        subtitle: '',
        initialValue: null,
        options: [],
        sections: [],
        componentProps: {},
        validation: { required: false },
        errorMessages: {},
        otherInputConfig: null,
        toggleCheckboxConfig: null,
        meta: { protected: false },
        modals: {}, // For modals triggered by this question
        defaultValue: undefined, // Specifically for hiddenInput, but can be generalized
        layout: { stepName: '' }
    });

    // State for QuizQuestion link properties
    const [questionOrder, setQuestionOrder] = useState(1);
    const [nextStepId, setNextStepId] = useState('');
    const [prevStepId, setPrevStepId] = useState('');
    const [nextButtonText, setNextButtonText] = useState('');
    const [prevButtonText, setPrevButtonText] = useState('');
    const [nextStepLogicJsonString, setNextStepLogicJsonString] = useState('');
    const [previousStepLogicJsonString, setPreviousStepLogicJsonString] = useState('');

    // Edit Quiz Question Modal State
    const [showEditQuizQuestionModal, setShowEditQuizQuestionModal] = useState(false);
    const [editingQuizQuestion, setEditingQuizQuestion] = useState(null);
    const [editingStandaloneQuestionId, setEditingStandaloneQuestionId] = useState(null);


    // UI State
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const fetchAvailableQuestions = useCallback(async () => {
        if (!token) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/questions`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Failed to fetch available questions');
            const data = await response.json();
            setAvailableQuestions(data || []);
        } catch (err) {
            setError(prev => prev ? `${prev}\nFailed to fetch available questions: ${err.message}` : `Failed to fetch available questions: ${err.message}`);
        }
    }, [token]);


    useEffect(() => {
        fetchAvailableQuestions();
    }, [fetchAvailableQuestions]);

    const fetchQuizzes = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/quizzes`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Error al obtener los cuestionarios');
            const data = await response.json();
            setQuizzes(data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchQuizzes();
    }, [fetchQuizzes]);

    const handleCreateQuiz = async (e) => {
        e.preventDefault();
        if (!newQuizName.trim()) {
            setError("El nombre del cuestionario es obligatorio.");
            return;
        }
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/quizzes`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ name: newQuizName, description: newQuizDescription }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Error al crear el cuestionario');
            }
            setSuccessMessage(`Cuestionario "${data.name}" creado con éxito. Ahora puedes añadir preguntas.`);
            setNewQuizName('');
            setNewQuizDescription('');
            await fetchQuizzes();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectQuizToManage = (quiz) => {
        fetchQuizDetails(quiz.id);
    };

    const handleUpdateQuizDetails = async (e) => {
        e.preventDefault();
        if (!activeQuiz || !editingQuizName.trim()) {
            setError("Se requiere un cuestionario activo y un nombre para actualizar.");
            return;
        }
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/quizzes/${activeQuiz.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ name: editingQuizName, description: editingQuizDescription }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Error al actualizar el cuestionario');
            }
            setActiveQuiz(data);
            setSuccessMessage(`Cuestionario "${data.name}" actualizado con éxito.`);
            await fetchQuizzes();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteQuiz = async (quizIdToDelete) => {
        if (!window.confirm("¿Estás seguro de que quieres eliminar este cuestionario completo y todas las preguntas asociadas? Esta acción no se puede deshacer.")) return;
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/quizzes/${quizIdToDelete}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Error al eliminar el cuestionario');
            }
            setSuccessMessage('Cuestionario eliminado con éxito.');
            await fetchQuizzes();
            if (activeQuiz && activeQuiz.id === quizIdToDelete) {
                setActiveQuiz(null);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const resetQuestionModalForm = () => {
        setSelectedExistingQuestionId('');
        setCurrentQuestionData({
            id: '', type: 'textInput', title: '', subtitle: '', initialValue: null,
            options: [], sections: [], componentProps: {}, validation: { required: false },
            errorMessages: {}, otherInputConfig: null, toggleCheckboxConfig: null,
            modals: {},
            defaultValue: undefined,
            meta: { protected: false }, layout: { stepName: '' }
        });
        setQuestionOrder(questionsInQuiz.length > 0 ? Math.max(...questionsInQuiz.map(q => q.question_order || 0)) + 1 : 1);
        setNextStepId('');
        setPrevStepId('');
        setNextButtonText('');
        setPrevButtonText('');
        setNextStepLogicJsonString('');
        setPreviousStepLogicJsonString('');
        setEditingStandaloneQuestionId(null);
        setEditingQuizQuestion(null);
    };

    const handleOpenAddQuestionModal = () => {
        resetQuestionModalForm();
        setQuestionMode('selectExistingLink');
        setShowAddQuestionModal(true);
    };

    const handleCurrentQuestionDataChange = (field, value) => {
        setCurrentQuestionData(prev => ({ ...prev, [field]: value }));
    };

    const handleNestedQuestionDataChange = (path, value) => {
        setCurrentQuestionData(prev => {
            const keys = path.split('.');
            let temp = { ...prev };
            let current = temp;
            for (let i = 0; i < keys.length - 1; i++) {
                if (current[keys[i]] === undefined || current[keys[i]] === null || typeof current[keys[i]] !== 'object') {
                    current[keys[i]] = {};
                }
                current = current[keys[i]];
            }
            current[keys[keys.length - 1]] = value;
            return temp;
        });
    };

    const handleAddOptionToCurrentQuestion = () => {
        setCurrentQuestionData(prev => ({
            ...prev,
            options: [
                ...(prev.options || []),
                {
                    value: '', label: '', text: '', icon: '', tooltip: '',
                    imageSrc: '', altText: '', mainText: '', subText: ''
                }
            ]
        }));
    };

    const handleCurrentQuestionOptionChange = (index, field, value) => {
        const updatedOptions = [...currentQuestionData.options];
        updatedOptions[index] = { ...updatedOptions[index], [field]: value };
        setCurrentQuestionData(prev => ({ ...prev, options: updatedOptions }));
    };

    const handleRemoveOptionFromCurrentQuestion = (index) => {
        setCurrentQuestionData(prev => ({
            ...prev,
            options: prev.options.filter((_, i) => i !== index)
        }));
    };

    const handleAddModalToCurrentQuestion = () => {
        setCurrentQuestionData(prev => ({
            ...prev,
            modals: {
                ...prev.modals,
                [`newModal${Object.keys(prev.modals || {}).length}`]: { type: '', configDetail: '' }
            }
        }));
    };

    const handleCurrentQuestionModalChange = (modalKey, field, value) => {
        setCurrentQuestionData(prev => ({
            ...prev,
            modals: {
                ...prev.modals,
                [modalKey]: { ...prev.modals[modalKey], [field]: field === 'config' ? JSON.parse(value || '{}') : value }
            }
        }));
    };

    const handleRemoveModalFromCurrentQuestion = (modalKeyToRemove) => {
        setCurrentQuestionData(prev => {
            const updatedModals = { ...prev.modals };
            delete updatedModals[modalKeyToRemove];
            return { ...prev, modals: updatedModals };
        });
    };

    const handleAddQuestionToQuizSubmit = async (e) => {
        e.preventDefault();
        if (questionMode !== 'editStandalone' && !activeQuiz) {
            setError("No hay un cuestionario activo para añadir preguntas.");
            return;
        }
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        let requestBody = {};

        if (questionMode === 'selectExistingLink' || questionMode === 'createNewStandaloneAndLink') {
            requestBody = {
                question_order: parseInt(questionOrder, 10),
                next_step_identifier: nextStepId || null,
                previous_step_identifier: prevStepId || null,
                next_button_text: nextButtonText || null,
                previous_button_text: prevButtonText || null,
            };
        }

        if (questionMode === 'selectExistingLink') {
            if (!selectedExistingQuestionId) {
                setError("Por favor, selecciona una pregunta existente."); setLoading(false); return;
            }
            const existingQ = availableQuestions.find(q => q.id === selectedExistingQuestionId);
            if (!existingQ) {
                setError("La pregunta seleccionada no se encontró."); setLoading(false); return;
            }
            requestBody.id = existingQ.id;
        } else if (questionMode === 'createNewStandaloneAndLink') {
            if (!currentQuestionData.id?.trim() || !currentQuestionData.title?.trim() || !currentQuestionData.type?.trim()) {
                setError("Para una nueva pregunta, se requiere identificador, título y tipo."); setLoading(false); return;
            }
            const filteredOptions = currentQuestionData.options?.filter(opt => opt.value?.trim() || opt.label?.trim());
            requestBody.question_data = {
                ...currentQuestionData,
                options: filteredOptions || [],
                componentProps: currentQuestionData.componentProps || {},
                validation: currentQuestionData.validation || {},
                errorMessages: currentQuestionData.errorMessages || {},
                meta: currentQuestionData.meta || {},
                layout: currentQuestionData.layout || {},
                modals: currentQuestionData.modals || {},
                defaultValue: currentQuestionData.type === 'hiddenInput' ? (currentQuestionData.initialValue !== null ? currentQuestionData.initialValue : 1) : undefined,
            };
        }

        try {
            if (questionMode === 'editStandalone') {
                if (!editingStandaloneQuestionId) {
                    setError("No se especificó ID de pregunta independiente para editar."); throw new Error("No standalone question ID for editing.");
                }
                const filteredOptions = currentQuestionData.options?.filter(opt => opt.value?.trim() || opt.label?.trim());
                const standaloneUpdateBody = {
                    ...currentQuestionData,
                    id: editingStandaloneQuestionId,
                    options: filteredOptions || [],
                    componentProps: currentQuestionData.componentProps || {},
                    validation: currentQuestionData.validation || {},
                    errorMessages: currentQuestionData.errorMessages || {},
                    meta: currentQuestionData.meta || {},
                    layout: currentQuestionData.layout || {},
                    modals: currentQuestionData.modals || {},
                    defaultValue: currentQuestionData.type === 'hiddenInput' ? (currentQuestionData.initialValue !== null ? currentQuestionData.initialValue : 1) : undefined,
                };
                const response = await fetch(`${API_BASE_URL}/api/questions/${editingStandaloneQuestionId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify(standaloneUpdateBody),
                });
                const data = await response.json();
                if (!response.ok) throw new Error(data.message || 'Error al actualizar la pregunta independiente');
                setSuccessMessage(`Pregunta independiente "${data.title || standaloneUpdateBody.title}" actualizada con éxito.`);
                await fetchAvailableQuestions();
                if (activeQuiz) await fetchQuizDetails(activeQuiz.id);
            } else {
                const response = await fetch(`${API_BASE_URL}/api/quizzes/${activeQuiz.id}/questions`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify(requestBody),
                });
                const data = await response.json();
                if (!response.ok) throw new Error(data.message || 'Failed to add question to quiz');
                setSuccessMessage('¡Pregunta añadida/vinculada al cuestionario con éxito!');
                await fetchQuizDetails(activeQuiz.id);
                if (questionMode === 'createNewStandaloneAndLink') {
                    await fetchAvailableQuestions();
                }
            }
            setShowAddQuestionModal(false);
            resetQuestionModalForm();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenEditStandaloneQuestionModal = (question) => {
        const questionToEdit = availableQuestions.find(q => q.id === question.id);
        if (!questionToEdit) {
            setError(`No se pudo encontrar la pregunta independiente con ID: ${question.id} para editar.`);
            console.error(`Standalone question ${question.id} not found in availableQuestions list.`);
            return;
        }
        const clonedQuestionData = JSON.parse(JSON.stringify(questionToEdit));
        setCurrentQuestionData({
            id: clonedQuestionData.id || '',
            type: clonedQuestionData.type || 'textInput',
            title: clonedQuestionData.title || '',
            subtitle: clonedQuestionData.subtitle || '',
            initialValue: clonedQuestionData.initialValue !== undefined ? clonedQuestionData.initialValue : null,
            options: Array.isArray(clonedQuestionData.options) ? clonedQuestionData.options : [],
            sections: Array.isArray(clonedQuestionData.sections) ? clonedQuestionData.sections : [],
            componentProps: typeof clonedQuestionData.componentProps === 'object' && clonedQuestionData.componentProps !== null ? clonedQuestionData.componentProps : {},
            validation: typeof clonedQuestionData.validation === 'object' && clonedQuestionData.validation !== null ? clonedQuestionData.validation : { required: false },
            errorMessages: typeof clonedQuestionData.errorMessages === 'object' && clonedQuestionData.errorMessages !== null ? clonedQuestionData.errorMessages : {},
            otherInputConfig: typeof clonedQuestionData.otherInputConfig === 'object' ? clonedQuestionData.otherInputConfig : null,
            toggleCheckboxConfig: typeof clonedQuestionData.toggleCheckboxConfig === 'object' ? clonedQuestionData.toggleCheckboxConfig : null,
            meta: typeof clonedQuestionData.meta === 'object' && clonedQuestionData.meta !== null ? clonedQuestionData.meta : { protected: false },
            layout: typeof clonedQuestionData.layout === 'object' && clonedQuestionData.layout !== null ? clonedQuestionData.layout : { stepName: '' },
            modals: typeof clonedQuestionData.modals === 'object' && clonedQuestionData.modals !== null ? clonedQuestionData.modals : {},
            defaultValue: clonedQuestionData.defaultValue, // Load defaultValue
        });
        setEditingStandaloneQuestionId(question.id);
        setQuestionMode('editStandalone');
        setShowAddQuestionModal(true);
    };

    const handleOpenEditQuizQuestionModal = (quizQuestionToEdit) => {
        setEditingQuizQuestion(quizQuestionToEdit);
        setQuestionOrder(quizQuestionToEdit.question_order);

        // Populate simple navigation fields from the 'navigation' object if it exists,
        // or directly from quizQuestionToEdit if 'navigation' is not present (for backward compatibility or direct properties)
        setNextStepId(quizQuestionToEdit.navigation?.nextStepId || quizQuestionToEdit.next_step_identifier || '');
        setPrevStepId(quizQuestionToEdit.navigation?.previousStepId || quizQuestionToEdit.previous_step_identifier || '');
        setNextButtonText(quizQuestionToEdit.navigation?.nextButtonText || quizQuestionToEdit.next_button_text || '');
        setPrevButtonText(quizQuestionToEdit.navigation?.previousButtonText || quizQuestionToEdit.previous_button_text || '');

        // Populate logic fields from the nested 'navigation' object as per the backend function
        setNextStepLogicJsonString(quizQuestionToEdit.navigation?.nextStepLogic ? JSON.stringify(quizQuestionToEdit.navigation.nextStepLogic, null, 2) : '');
        setPreviousStepLogicJsonString(quizQuestionToEdit.navigation?.previousStepLogic ? JSON.stringify(quizQuestionToEdit.navigation.previousStepLogic, null, 2) : '');

        const standaloneQuestionToEdit = availableQuestions.find(q => q.id === quizQuestionToEdit.id);
        if (standaloneQuestionToEdit) {
            const clonedQuestionData = JSON.parse(JSON.stringify(standaloneQuestionToEdit));
            setCurrentQuestionData({
                id: clonedQuestionData.id || '',
                type: clonedQuestionData.type || 'textInput',
                title: clonedQuestionData.title || '',
                subtitle: clonedQuestionData.subtitle || '',
                initialValue: clonedQuestionData.initialValue !== undefined ? clonedQuestionData.initialValue : null,
                options: Array.isArray(clonedQuestionData.options) ? clonedQuestionData.options : [],
                sections: Array.isArray(clonedQuestionData.sections) ? clonedQuestionData.sections : [],
                componentProps: typeof clonedQuestionData.componentProps === 'object' && clonedQuestionData.componentProps !== null ? clonedQuestionData.componentProps : {},
                validation: typeof clonedQuestionData.validation === 'object' && clonedQuestionData.validation !== null ? clonedQuestionData.validation : { required: false },
                errorMessages: typeof clonedQuestionData.errorMessages === 'object' && clonedQuestionData.errorMessages !== null ? clonedQuestionData.errorMessages : {},
                otherInputConfig: typeof clonedQuestionData.otherInputConfig === 'object' ? clonedQuestionData.otherInputConfig : null,
                toggleCheckboxConfig: typeof clonedQuestionData.toggleCheckboxConfig === 'object' ? clonedQuestionData.toggleCheckboxConfig : null,
                meta: typeof clonedQuestionData.meta === 'object' && clonedQuestionData.meta !== null ? clonedQuestionData.meta : { protected: false },
                layout: typeof clonedQuestionData.layout === 'object' && clonedQuestionData.layout !== null ? clonedQuestionData.layout : { stepName: '' },
                modals: typeof clonedQuestionData.modals === 'object' && clonedQuestionData.modals !== null ? clonedQuestionData.modals : {},
                defaultValue: clonedQuestionData.defaultValue, // Load defaultValue
            });
            setEditingStandaloneQuestionId(standaloneQuestionToEdit.id);
        } else {
            console.error(`Standalone question with ID ${quizQuestionToEdit.id} not found. Cannot populate form for content editing.`);
            resetQuestionModalForm(); // Reset relevant parts, but keep link-specific data like order, next/prev IDs
            setEditingStandaloneQuestionId(null);
        }
        setError(null);
        setSuccessMessage(null);
        setShowEditQuizQuestionModal(true);
    };

    const handleUpdateQuizQuestionSubmit = async (e) => {
        e.preventDefault();

        if (!activeQuiz || !editingQuizQuestion || editingQuizQuestion.quiz_question_link_id === undefined) {
            setError("No hay cuestionario activo o enlace de pregunta seleccionado para editar, o falta el ID del enlace.");
            return;
        }
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        const requestBody = {
            question_order: parseInt(questionOrder, 10),
            next_step_identifier: nextStepId || null,
            previous_step_identifier: prevStepId || null,
            next_button_text: nextButtonText || null,
            previous_button_text: prevButtonText || null,
        };

        try {
            requestBody.next_step_logic = nextStepLogicJsonString.trim() ? JSON.parse(nextStepLogicJsonString) : null;
        } catch (parseError) {
            setError(`Error al analizar JSON de Lógica del Siguiente Paso: ${parseError.message}`);
            setLoading(false); return;
        }
        try {
            requestBody.previous_step_logic = previousStepLogicJsonString.trim() ? JSON.parse(previousStepLogicJsonString) : null;
        } catch (parseError) {
            setError(`Error al analizar JSON de Lógica del Paso Anterior: ${parseError.message}`);
            setLoading(false); return;
        }

        // If editingStandaloneQuestionId is set and matches the current linked question's identifier,
        // it implies the modal allowed editing standalone question content, so include question_data.
        if (editingStandaloneQuestionId && editingStandaloneQuestionId === editingQuizQuestion.id && currentQuestionData.id === editingStandaloneQuestionId) {
            const filteredOptions = currentQuestionData.options?.filter(opt => opt.value?.trim() || opt.label?.trim());
            requestBody.question_data = {
                ...currentQuestionData,
                id: editingStandaloneQuestionId, // Ensure this is the identifier of the question to update
                options: filteredOptions || [],
                componentProps: currentQuestionData.componentProps || {},
                validation: currentQuestionData.validation || { required: false },
                errorMessages: currentQuestionData.errorMessages || {},
                meta: currentQuestionData.meta || { protected: false },
                layout: currentQuestionData.layout || { stepName: '' },
                modals: currentQuestionData.modals || {},
                defaultValue: currentQuestionData.type === 'hiddenInput' ? (currentQuestionData.initialValue !== null ? currentQuestionData.initialValue : 1) : undefined,
            };
        }


        try {
            const response = await fetch(`${API_BASE_URL}/api/quizzes/${activeQuiz.id}/questions/${editingQuizQuestion.quiz_question_link_id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(requestBody),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Error al actualizar la pregunta en el cuestionario');
            }
            setSuccessMessage('Detalles de la pregunta en el cuestionario actualizados con éxito.');
            fetchQuizDetails(activeQuiz.id);
            setShowEditQuizQuestionModal(false);
            resetQuestionModalForm(); // Reset form including editingQuizQuestion and editingStandaloneQuestionId
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchQuizDetails = async (quizId) => {
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/quizzes/${quizId}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Error al obtener los detalles del cuestionario');
            const data = await response.json();
            setActiveQuiz(data);
            setEditingQuizName(data.name);
            setEditingQuizDescription(data.description || '');
            setQuestionsInQuiz(data.questions || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveQuestionFromQuiz = async (quizQuestionLinkIdToRemove) => {
        if (!activeQuiz || !quizQuestionLinkIdToRemove) return;
        if (!window.confirm("¿Estás seguro de que quieres eliminar esta pregunta del cuestionario?")) return;

        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/quizzes/${activeQuiz.id}/questions/${quizQuestionLinkIdToRemove}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Error al eliminar la pregunta');
            }
            setSuccessMessage('Pregunta eliminada del cuestionario.');
            fetchQuizDetails(activeQuiz.id);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const renderStandaloneQuestionFormFields = () => {
        const commonTypesWithOptions = ['radioGroup', 'selectableOption', 'multiSelect', 'checkboxGroupWithOther', 'selectableOptionWithOther', 'selectableImageOption'];
        const isOptionType = commonTypesWithOptions.includes(currentQuestionData.type);
        const isHiddenInputType = currentQuestionData.type === 'hiddenInput';

        return (
            <>
                <div className="mb-3">
                    <label htmlFor="currentQuestionId" className="form-label">Identificador de Pregunta (ej: paso_unico_1) *</label>
                    <input
                        type="text"
                        className="form-control"
                        id="currentQuestionId" value={currentQuestionData.id || ''}
                        onChange={(e) => handleCurrentQuestionDataChange('id', e.target.value)}
                        required={questionMode !== 'editStandalone' && questionMode !== 'editQuizQuestionLink'}
                        disabled={questionMode === 'editStandalone' || questionMode === 'editQuizQuestionLink'}
                    />
                </div>
                <div className="mb-3">
                    <label htmlFor="currentQuestionType" className="form-label">Tipo de Pregunta *</label>
                    <select className="form-select" id="currentQuestionType" value={currentQuestionData.type} onChange={(e) => handleCurrentQuestionDataChange('type', e.target.value)} required>
                        <option value="textInput">Text Input</option>
                        <option value="numberInput">Number Input</option>
                        <option value="textArea">Text Area</option>
                        <option value="radioGroup">Radio Group</option>
                        <option value="selectableOption">Selectable Option</option>
                        <option value="multiSelect">Multi-Select</option>
                        <option value="checkboxGroupWithOther">Checkbox Group with Other</option>
                        <option value="selectableOptionWithOther">Selectable Option with Other</option>
                        <option value="multiSectionCheckboxGroup">Multi-Section Checkbox Group</option>
                        <option value="multiSectionSelectableOption">Multi-Section Selectable Option</option>
                        <option value="selectableImageOption">Selectable Image Option</option>
                        <option value="numberInputWithToggle">Number Input with Toggle</option>
                        <option value="hiddenInput">Hidden Input</option>
                        <option value="dateRangePicker">Date Range Picker</option>
                    </select>
                </div>
                <div className="mb-3">
                    <label htmlFor="currentQuestionTitle" className="form-label">Título de la Pregunta *</label>
                    <input type="text" className="form-control" id="currentQuestionTitle" value={currentQuestionData.title || ''} onChange={(e) => handleCurrentQuestionDataChange('title', e.target.value)} required />
                </div>
                <div className="mb-3">
                    <label htmlFor="currentQuestionSubtitle" className="form-label">Subtítulo de la Pregunta</label>
                    <input type="text" className="form-control" id="currentQuestionSubtitle" value={currentQuestionData.subtitle || ''} onChange={(e) => handleCurrentQuestionDataChange('subtitle', e.target.value)} />
                </div>
                {isHiddenInputType && (
                    <div className="mb-3">
                        <label htmlFor="currentQuestionInitialValue" className="form-label">Valor por Defecto (para Hidden Input)</label>
                        {/* Using initialValue state field for simplicity, will be mapped to defaultValue on save */}
                        <input type="text" className="form-control" id="currentQuestionInitialValue" value={currentQuestionData.initialValue === null ? '' : currentQuestionData.initialValue} onChange={(e) => handleCurrentQuestionDataChange('initialValue', e.target.value)} />
                    </div>
                )}

                <div className="mb-3">
                    <label htmlFor="cqdLayoutStepName" className="form-label">Diseño: Nombre del Paso</label>
                    <input type="text" className="form-control" id="cqdLayoutStepName" value={currentQuestionData.layout?.stepName || ''} onChange={(e) => handleNestedQuestionDataChange('layout.stepName', e.target.value)} />
                </div>
                <div className="form-check mb-3">
                    <input className="form-check-input" type="checkbox" id="cqdMetaProtected" checked={currentQuestionData.meta?.protected || false} onChange={(e) => handleNestedQuestionDataChange('meta.protected', e.target.checked)} />
                    <label className="form-check-label" htmlFor="cqdMetaProtected">
                        Meta: Is Protected?
                    </label>
                </div>

                {isOptionType && !isHiddenInputType && (
                    <div className="mb-3 p-3 border rounded bg-light">
                        <h6>Opciones</h6>
                        {currentQuestionData.options && currentQuestionData.options.map((opt, index) => (
                            <div key={index} className="row gx-2 mb-2 align-items-center p-2 border-bottom">
                                <div className="col-md-2 mb-2">
                                    <input type="text" className="form-control form-control-sm" placeholder="Valor (ej: opc_1)" value={opt.value || ''} onChange={(e) => handleCurrentQuestionOptionChange(index, 'value', e.target.value)} />
                                </div>
                                <div className="col-md-2 mb-2">
                                    <input type="text" className="form-control form-control-sm" placeholder="Etiqueta (para usuario)" value={opt.label || ''} onChange={(e) => handleCurrentQuestionOptionChange(index, 'label', e.target.value)} />
                                </div>
                                <div className="col-md-2 mb-2">
                                    <input type="text" className="form-control form-control-sm" placeholder="Texto (alternativo)" value={opt.text || ''} onChange={(e) => handleCurrentQuestionOptionChange(index, 'text', e.target.value)} />
                                </div>
                                <div className="col-md-2 mb-2">
                                    <input type="text" className="form-control form-control-sm" placeholder="Icono (emoji)" value={opt.icon || ''} onChange={(e) => handleCurrentQuestionOptionChange(index, 'icon', e.target.value)} />
                                </div>
                                <div className="col-md-3 mb-2">
                                    <input type="text" className="form-control form-control-sm" placeholder="Tooltip (ayuda)" value={opt.tooltip || ''} onChange={(e) => handleCurrentQuestionOptionChange(index, 'tooltip', e.target.value)} />
                                </div>
                                <div className="col-md-4 mb-2">
                                    <input type="text" className="form-control form-control-sm" placeholder="URL de Imagen" value={opt.imageSrc || ''} onChange={(e) => handleCurrentQuestionOptionChange(index, 'imageSrc', e.target.value)} />
                                </div>
                                <div className="col-md-3 mb-2">
                                    <input type="text" className="form-control form-control-sm" placeholder="Texto Principal (ej: tarjetas)" value={opt.mainText || ''} onChange={(e) => handleCurrentQuestionOptionChange(index, 'mainText', e.target.value)} />
                                </div>
                                <div className="col-md-3 mb-2">
                                    <input type="text" className="form-control form-control-sm" placeholder="Texto Secundario (ej: tarjetas)" value={opt.subText || ''} onChange={(e) => handleCurrentQuestionOptionChange(index, 'subText', e.target.value)} />
                                </div>
                                <div className="col-md-1 mb-2 text-end">
                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveOptionFromCurrentQuestion(index)}>X</button>
                                </div>
                            </div>
                        ))}
                        <button type="button" className="btn btn-sm btn-outline-checkin mt-2" onClick={handleAddOptionToCurrentQuestion}><AddIcon className="me-1" /> Añadir Opción</button>
                    </div>
                )}

                {/* Modals Management Section */}
                {!isHiddenInputType && <div className="mb-3 p-3 border rounded bg-light">
                    <h6>Ventanas Modales</h6>
                    {currentQuestionData.modals && Object.entries(currentQuestionData.modals).map(([modalKey, modalConfig]) => (
                        <div key={modalKey} className="row gx-2 mb-2 p-2 border-bottom">
                            <div className="col-md-3 mb-2">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Nombre Modal (ej: AgradecimientoModal)"
                                    value={modalKey}
                                    onChange={(e) => {
                                        const newKey = e.target.value;
                                        const oldConfig = currentQuestionData.modals[modalKey];
                                        setCurrentQuestionData(prev => {
                                            const updatedModals = { ...prev.modals };
                                            delete updatedModals[modalKey];
                                            updatedModals[newKey] = oldConfig;
                                            return { ...prev, modals: updatedModals };
                                        });
                                    }} />
                            </div>
                            <div className="col-md-7 mb-2">
                                <textarea className="form-control form-control-sm" rows="3" placeholder='Configuración Modal (JSON) ej: { "type": "AgradecimientoModal", "message": "¡Gracias!" }' value={JSON.stringify(modalConfig, null, 2)} onChange={(e) => handleCurrentQuestionModalChange(modalKey, 'config', e.target.value)} />
                            </div>
                            <div className="col-md-1 mb-2 text-end align-self-center">
                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveModalFromCurrentQuestion(modalKey)}>X</button>
                            </div>
                        </div>
                    ))}
                    <button type="button" className="btn btn-sm btn-outline-checkin mt-2" onClick={handleAddModalToCurrentQuestion}><AddIcon className="me-1" /> Añadir Modal</button>
                </div>}
            </>
        );
    };


    return (
        <div className="container mt-4">
            <h2>Administrador de Cuestionarios</h2>

            {error && <div className="alert alert-danger alert-dismissible fade show" role="alert">
                {error}
                <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close"></button>
            </div>}
            {successMessage && <div className="alert alert-success alert-dismissible fade show" role="alert">
                {successMessage}
                <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)} aria-label="Close"></button>
            </div>}
            {loading && <p>Cargando...</p>}

            {activeQuiz ? (
                <div className="card p-4">
                    <button className="btn btn-sm btn-outline-secondary mb-3" style={{ width: 'fit-content' }} onClick={() => { setActiveQuiz(null); setSuccessMessage(''); setError(''); }}>
                        &larr; Volver a lista de cuestionarios
                    </button>
                    <h3>Administrando cuestionario: <strong>{editingQuizName}</strong></h3>
                    <form onSubmit={handleUpdateQuizDetails} aria-labelledby="quizDetailsHeading">
                        <div className="mb-3">
                            <label htmlFor="editingQuizName" className="form-label">Nombre *</label>
                            <input
                                type="text"
                                className="form-control"
                                id="editingQuizName"
                                value={editingQuizName}
                                onChange={(e) => setEditingQuizName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="editingQuizDescription" className="form-label">Descripción</label>
                            <textarea
                                className="form-control"
                                id="editingQuizDescription"
                                rows="3"
                                value={editingQuizDescription}
                                onChange={(e) => setEditingQuizDescription(e.target.value)}
                            ></textarea>
                        </div>
                        <button type="submit" className="btn btn-checkin" disabled={loading}>
                            {loading ? 'Guardando...' : 'Actualizar cuestionario'}
                        </button>
                    </form>

                    <hr />
                    <h4>Preguntas en el cuestionario</h4>
                    {questionsInQuiz.length === 0 ? (
                        <p>No hay preguntas agregadas aún.</p>
                    ) : (
                        <ul className="list-group list-group-flush mb-3">
                            {questionsInQuiz.sort((a, b) => (a.question_order || 0) - (b.question_order || 0)).map(q => (
                                <li key={q.quiz_question_link_id || q.id} className="list-group-item d-flex justify-content-between align-items-center">
                                    <span>{q.question_order}. {q.title} ({q.type}) - TID: {q.id} (ID: {q.quiz_question_link_id})</span>
                                    <div>
                                        <button
                                            className="btn btn-sm btn-outline-secondary me-2"
                                            onClick={() => handleOpenEditQuizQuestionModal(q)}
                                            disabled={loading}
                                            title="Editar Propiedades del Enlace y Contenido de la Pregunta"
                                        >
                                            <EditIcon />
                                        </button>
                                        <button
                                            className="btn btn-sm btn-info me-2"
                                            onClick={() => handleOpenEditStandaloneQuestionModal(q)}
                                            disabled={loading}
                                            title="Editar Pregunta Original (Independiente)"
                                        >
                                            <EditIcon />
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => handleRemoveQuestionFromQuiz(q.quiz_question_link_id)}
                                            disabled={loading}
                                            title="Eliminar del Cuestionario"
                                        >
                                            <DeleteIcon />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                    <button className="btn btn-success" onClick={handleOpenAddQuestionModal} disabled={loading}>
                        Añadir pregunta al cuestionario
                    </button>
                </div>
            ) : (
                <>
                    <form onSubmit={handleCreateQuiz} className="card p-4 mb-4" aria-labelledby="createQuizHeading">
                        <h3>Crear nuevo cuestionario</h3>
                        <div className="mb-3">
                            <label htmlFor="newQuizName" className="form-label">Código Cuestionario *</label>
                            <input type="text" className="form-control" id="newQuizName" value={newQuizName} onChange={(e) => setNewQuizName(e.target.value)} required />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="newQuizDescription" className="form-label">Descripción Cuestionario</label>
                            <textarea className="form-control" id="newQuizDescription" rows="3" value={newQuizDescription} onChange={(e) => setNewQuizDescription(e.target.value)}></textarea>
                        </div>
                        <button type="submit" className="btn btn-checkin" disabled={loading}> {loading ? 'Creando...' : 'Crear cuestionario'} </button>
                    </form>

                    <div className="card p-4">
                        <h3>Cuestionarios existentes</h3>
                        {quizzes.length === 0 && !loading ? <p>No se encontraron cuestionarios</p> : null}
                        <ul className="list-group list-group-flush" aria-label="Lista de cuestionarios existentes">
                            {quizzes.map(quiz => (
                                <li key={quiz.id} className="list-group-item d-flex justify-content-between align-items-center">
                                    {quiz.name}
                                    <div>
                                        <button className="btn btn-sm btn-outline-checkin me-2" onClick={() => handleSelectQuizToManage(quiz)} disabled={loading}>Editar</button>
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteQuiz(quiz.id)} disabled={loading}>Borrar</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </>
            )}

            {/* Modal for Adding/Creating/Editing Questions */}
            {showAddQuestionModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl">
                        <div className="modal-content">
                            <form onSubmit={handleAddQuestionToQuizSubmit}>
                                <div className="modal-header">
                                    <h5 className="modal-title" id="addQuestionModalLabel">
                                        {questionMode === 'editStandalone' ? `Editar Pregunta Independiente: ${currentQuestionData.title || editingStandaloneQuestionId}` :
                                            (activeQuiz ? `Añadir/Crear Pregunta para "${activeQuiz.name}"` : 'Gestionar Pregunta')}
                                    </h5>
                                    <button type="button" className="btn-close" onClick={() => { setShowAddQuestionModal(false); resetQuestionModalForm(); }} aria-label="Cerrar"></button>
                                </div>
                                <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                                    {error && <div className="alert alert-danger">{error}</div>}

                                    {questionMode !== 'editStandalone' && (
                                        <div className="mb-3">
                                            <label className="form-label">Mode:</label>
                                            <div role="radiogroup" aria-labelledby="questionModeLabel">
                                                <div className="form-check form-check-inline">
                                                    <input className="form-check-input" type="radio" name="questionModeRadio" id="selectExistingLink" value="selectExistingLink" checked={questionMode === 'selectExistingLink'} onChange={(e) => setQuestionMode(e.target.value)} />
                                                    <label className="form-check-label" htmlFor="selectExistingLink">Seleccionar Pregunta Existente para Vincular</label>
                                                </div>
                                                <div className="form-check form-check-inline">
                                                    <input className="form-check-input" type="radio" name="questionModeRadio" id="createNewStandaloneAndLink" value="createNewStandaloneAndLink" checked={questionMode === 'createNewStandaloneAndLink'} onChange={(e) => setQuestionMode(e.target.value)} />
                                                    <label className="form-check-label" htmlFor="createNewStandaloneAndLink">Crear Nueva Pregunta Independiente y Vincular</label>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {questionMode === 'selectExistingLink' && (
                                        <div className="mb-3">
                                            <label htmlFor="existingQuestion" className="form-label">Seleccionar Pregunta *</label>
                                            <select
                                                className="form-select"
                                                id="existingQuestion"
                                                value={selectedExistingQuestionId}
                                                onChange={(e) => setSelectedExistingQuestionId(e.target.value)}
                                                required={questionMode === 'selectExistingLink'}
                                            >
                                                <option value="">-- Selecciona una pregunta --</option>
                                                {availableQuestions.map(q => (
                                                    <option key={q.id} value={q.id}>{q.title} (ID: {q.id}, Tipo: {q.type})</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {(questionMode === 'createNewStandaloneAndLink' || questionMode === 'editStandalone') && renderStandaloneQuestionFormFields()}

                                    {(questionMode === 'selectExistingLink' || questionMode === 'createNewStandaloneAndLink') && (
                                        <>
                                            <hr />
                                            <h5>Detalles Específicos del Cuestionario para este Enlace de Pregunta:</h5>
                                            <div className="mb-3">
                                                <label htmlFor="questionOrder" className="form-label">Orden de la Pregunta en el Cuestionario *</label>
                                                <input type="number" className="form-control" id="questionOrder" value={questionOrder} onChange={(e) => setQuestionOrder(e.target.value)} required min="1" />
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="prevStepId" className="form-label">Identificador del Paso Anterior (para este enlace)</label>
                                                    <input type="text" className="form-control" id="prevStepId" value={prevStepId} onChange={(e) => setPrevStepId(e.target.value)} placeholder="ej: paso_intro o id_pregunta_anterior" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="nextStepId" className="form-label">Identificador del Siguiente Paso (para este enlace)</label>
                                                    <input type="text" className="form-control" id="nextStepId" value={nextStepId} onChange={(e) => setNextStepId(e.target.value)} placeholder="ej: id_pregunta_2 o paso_resumen" />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="prevButtonText" className="form-label">Texto del Botón Anterior</label>
                                                    <input type="text" className="form-control" id="prevButtonText" value={prevButtonText} onChange={(e) => setPrevButtonText(e.target.value)} placeholder="ej: Atrás" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="nextButtonText" className="form-label">Texto del Botón Siguiente</label>
                                                    <input type="text" className="form-control" id="nextButtonText" value={nextButtonText} onChange={(e) => setNextButtonText(e.target.value)} placeholder="ej: Continuar" />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => { setShowAddQuestionModal(false); resetQuestionModalForm(); }}>Cerrar</button>
                                    <button type="submit" className="btn btn-checkin" disabled={loading}>
                                        {loading ? 'Procesando...' :
                                            (questionMode === 'editStandalone' ? 'Guardar Cambios Pregunta Independiente' :
                                                (questionMode === 'createNewStandaloneAndLink' ? 'Crear y Añadir al Cuestionario' : 'Añadir al Cuestionario'))}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Quiz Question Link Modal */}
            {showEditQuizQuestionModal && activeQuiz && editingQuizQuestion && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl">
                        <div className="modal-content">
                            <form onSubmit={handleUpdateQuizQuestionSubmit}>
                                <div className="modal-header">
                                    <h5 className="modal-title" id="editQuizQuestionModalLabel">Editar Enlace de Pregunta en Cuestionario: "{editingQuizQuestion.title}"</h5>
                                    <button type="button" className="btn-close" onClick={() => { setShowEditQuizQuestionModal(false); resetQuestionModalForm(); }} aria-label="Cerrar"></button>
                                </div>
                                <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                                    {error && <div className="alert alert-danger">{error}</div>}

                                    {/* Section to Edit Standalone Question Content */}
                                    <h5>Contenido de la Pregunta Independiente (Editable):</h5>
                                    {editingStandaloneQuestionId && currentQuestionData.id === editingStandaloneQuestionId ? (
                                        renderStandaloneQuestionFormFields()
                                    ) : (
                                        <p>Cargando contenido de la pregunta o contenido no disponible para editar...</p>
                                    )}

                                    <hr />
                                    <h5>Detalles Específicos del Enlace del Cuestionario:</h5>
                                    <div className="mb-3">
                                        <label htmlFor="editQuestionOrder" className="form-label">Orden de la Pregunta en el Cuestionario *</label>
                                        <input type="number" className="form-control" id="editQuestionOrder" value={questionOrder} onChange={(e) => setQuestionOrder(e.target.value)} required min="1" />
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="editPrevStepId" className="form-label">Identificador del Paso Anterior</label>
                                            <input type="text" className="form-control" id="editPrevStepId" value={prevStepId} onChange={(e) => setPrevStepId(e.target.value)} placeholder="ej: paso_intro" />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="editNextStepId" className="form-label">Identificador del Siguiente Paso</label>
                                            <input type="text" className="form-control" id="editNextStepId" value={nextStepId} onChange={(e) => setNextStepId(e.target.value)} placeholder="ej: pregunta_2" />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="editPrevButtonText" className="form-label">Texto del Botón Anterior</label>
                                            <input type="text" className="form-control" id="editPrevButtonText" value={prevButtonText} onChange={(e) => setPrevButtonText(e.target.value)} placeholder="ej: Atrás" />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="editNextButtonText" className="form-label">Texto del Botón Siguiente</label>
                                            <input type="text" className="form-control" id="editNextButtonText" value={nextButtonText} onChange={(e) => setNextButtonText(e.target.value)} placeholder="ej: Continuar" />
                                        </div>
                                    </div>
                                    {/* Logic Configuration Fields */}
                                    <div className="mb-3">
                                        <label htmlFor="nextStepLogic" className="form-label">Lógica del Siguiente Paso (JSON)</label>
                                        <textarea
                                            className="form-control"
                                            id="nextStepLogic"
                                            rows="5"
                                            value={nextStepLogicJsonString}
                                            onChange={(e) => setNextStepLogicJsonString(e.target.value)}
                                            placeholder='Ej: { "action": "navigate", "defaultNextStepId": "step_2", "conditions": [...] }'
                                        ></textarea>
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="previousStepLogic" className="form-label">Lógica del Paso Anterior (JSON)</label>
                                        <textarea
                                            className="form-control"
                                            id="previousStepLogic"
                                            rows="5"
                                            value={previousStepLogicJsonString}
                                            onChange={(e) => setPreviousStepLogicJsonString(e.target.value)}
                                            placeholder='Ej: { "action": "navigate", "defaultPreviousStepId": "step_1", "branchingOn": "question_id", ... }'
                                        ></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => { setShowEditQuizQuestionModal(false); resetQuestionModalForm(); }}>Cerrar</button>
                                    <button type="submit" className="btn btn-checkin" disabled={loading}>
                                        {loading ? 'Guardando...' : 'Guardar Cambios'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuizManagerAdminScreen;
